/**
 * Liveness watchdog for a WebSocket that cannot be trusted to close itself.
 *
 * A browser WebSocket reports `readyState === OPEN` for as long as the local
 * TCP socket believes it has a peer, and that belief survives every failure
 * that produces no FIN and no RST: a laptop suspended and resumed, a wifi-to-
 * cellular handover, a VPN reconnect, a NAT or proxy that expires an idle flow
 * without telling either end. In all of those the socket is dead, `send()`
 * still succeeds — the bytes go into a send buffer that will never drain —
 * `onclose` never fires, and any reconnect ladder hung off `onclose` therefore
 * never runs. The channel is silently one-way-dead until the page is reloaded.
 *
 * Sending a periodic ping does not fix that on its own; it is the *answer* that
 * carries the information. Both of this application's channels already reply to
 * a ping — the terminal gateway echoes one, the event hub sends a pong — and
 * both were sending pings and discarding the reply, which measures nothing.
 * This is the piece that reads it.
 *
 * The rule is deliberately two-stage: prolonged silence only earns the peer a
 * direct question, and only an unanswered question is death. A one-stage
 * timeout would have to be set above the worst tolerable stall, which is the
 * same as being slow; asking first means the threshold can be tight without
 * turning a slow network into a dropped connection.
 */

export interface WatchdogOptions {
  /**
   * How long the channel may be silent before the peer is asked directly.
   * Must exceed the interval at which the server sends unsolicited frames, or
   * every quiet moment costs a probe.
   */
  silenceMs: number
  /** How long a probe may go unanswered before the channel is presumed dead. */
  probeMs: number
  /** How often the two deadlines above are evaluated. */
  tickMs: number
  /** Sends one probe frame. Anything the peer is obliged to answer will do. */
  probe: () => void
  /** Called at most once per `start()`, when the channel is judged dead. */
  onDead: () => void
  /** Injected for tests. */
  now?: () => number
}

export interface Watchdog {
  /** Begins watching, counting from now. */
  start: () => void
  /** Records that a frame arrived. Any frame counts, including the probe's answer. */
  alive: () => void
  /**
   * Probes immediately, whatever the silence timer says.
   *
   * This is what a `visibilitychange` or an `online` event should call. Those
   * are the two moments a socket is most likely to have died unobserved, and
   * they are also the moments an operator is looking at the pane — waiting out
   * the ordinary silence budget there would show them the very freeze this
   * exists to prevent.
   */
  poke: () => void
  /** Stops watching. Safe to call more than once. */
  stop: () => void
}

export function createWatchdog(opts: WatchdogOptions): Watchdog {
  const now = opts.now ?? (() => Date.now())

  let timer: ReturnType<typeof setInterval> | null = null
  let lastSeen = 0
  /** When the outstanding probe was sent, or null when none is outstanding. */
  let probedAt: number | null = null
  /** Latches so a dead channel is reported once, not once per tick. */
  let dead = false

  const sendProbe = () => {
    probedAt = now()
    opts.probe()
  }

  const stop = () => {
    if (timer) clearInterval(timer)
    timer = null
  }

  const tick = () => {
    if (dead) return
    const t = now()
    if (probedAt !== null) {
      if (t - probedAt >= opts.probeMs) {
        dead = true
        stop()
        opts.onDead()
      }
      return
    }
    if (t - lastSeen >= opts.silenceMs) sendProbe()
  }

  return {
    start() {
      stop()
      dead = false
      probedAt = null
      lastSeen = now()
      timer = setInterval(tick, opts.tickMs)
    },
    alive() {
      lastSeen = now()
      // A frame of any kind proves the path carries traffic, so an outstanding
      // probe has been overtaken by its own answer.
      probedAt = null
    },
    poke() {
      if (dead || !timer) return
      if (probedAt === null) sendProbe()
      // An already-outstanding probe is left to its own deadline: restarting it
      // here would let a page that fires visibilitychange repeatedly hold a dead
      // channel open indefinitely by continually resetting the clock.
    },
    stop,
  }
}
